import { useCallback, useState } from 'react';
import notify from '../../../shared/feedback/notifications';
import ZentuvoInvoicingApi, { IProcountorInvoicePreviewGroup } from '../ZentuvoInvoicingApi';
import { buildProcountorIdAssignmentPhrase, copyTextToClipboard, getErrorMessage } from '../procountorInvoiceHelpers';

type UseProcountorIdGenerationResult = {
    generatedProcountorIds: { [groupId: string]: string };
    isGeneratingProcountorId: (groupId: string) => boolean;
    handleGenerateProcountorId: (groupId: string) => Promise<void>;
    handleCopyGeneratedProcountorId: (groupId: string) => Promise<void>;
    handleCopyProcountorIdAssignmentPhrase: (group: IProcountorInvoicePreviewGroup) => Promise<void>;
};

export function useProcountorIdGeneration(): UseProcountorIdGenerationResult {
    const [generatedProcountorIds, setGeneratedProcountorIds] = useState<{ [groupId: string]: string }>({});
    const [generatingGroupIds, setGeneratingGroupIds] = useState<string[]>([]);

    const isGeneratingProcountorId = useCallback(
        (groupId: string): boolean => generatingGroupIds.indexOf(groupId) >= 0,
        [generatingGroupIds]
    );

    const handleGenerateProcountorId = useCallback(
        async (groupId: string) => {
            if (!groupId || generatingGroupIds.indexOf(groupId) >= 0) {
                return;
            }

            setGeneratingGroupIds((currentGroupIds) => currentGroupIds.concat(groupId));
            try {
                const response = await ZentuvoInvoicingApi.generateProcountorId();
                const generatedProcountorId = response && response.ProcountorId ? String(response.ProcountorId) : '';

                if (!generatedProcountorId) {
                    notify.failure('Generate ProcountorId API returned an empty id');
                    return;
                }

                setGeneratedProcountorIds((currentIds) => ({
                    ...currentIds,
                    [groupId]: generatedProcountorId
                }));

                notify.success(`Generated ProcountorId: ${generatedProcountorId}`);
            } catch (error) {
                notify.failure(getErrorMessage(error, 'Failed to generate ProcountorId'));
                console.error('generateProcountorId error', error);
            } finally {
                setGeneratingGroupIds((currentGroupIds) => currentGroupIds.filter((value) => value !== groupId));
            }
        },
        [generatingGroupIds]
    );

    const handleCopyGeneratedProcountorId = useCallback(
        async (groupId: string) => {
            const generatedProcountorId = generatedProcountorIds[groupId];
            if (!generatedProcountorId) {
                return;
            }

            try {
                await copyTextToClipboard(generatedProcountorId);
                notify.success('ProcountorId copied to clipboard');
            } catch (error) {
                notify.failure('Failed to copy ProcountorId to clipboard');
                console.error('copy generated ProcountorId error', error);
            }
        },
        [generatedProcountorIds]
    );

    const handleCopyProcountorIdAssignmentPhrase = useCallback(
        async (group: IProcountorInvoicePreviewGroup) => {
            const effectiveProcountorId = generatedProcountorIds[group.GroupId] || group.ProcountorId;
            if (!effectiveProcountorId) {
                return;
            }

            const phrase = buildProcountorIdAssignmentPhrase(group.InvoiceReceiver, group.BrokerFirm, effectiveProcountorId);

            try {
                await copyTextToClipboard(phrase);
                notify.success('Phrase copied to clipboard');
            } catch (error) {
                notify.failure('Failed to copy phrase to clipboard');
                console.error('copy ProcountorId assignment phrase error', error);
            }
        },
        [generatedProcountorIds]
    );

    return {
        generatedProcountorIds,
        isGeneratingProcountorId,
        handleGenerateProcountorId,
        handleCopyGeneratedProcountorId,
        handleCopyProcountorIdAssignmentPhrase
    };
}
